// VRG-740 — react-query hooks for the content-ideas queue (GTM → Insights → Ideas, VRG-751).
// The VERAfied guest hooks that lived here went with the second guest list (VRG-752); the one
// list has its own hooks in `useVerafied.ts`.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AddContentIdeaSourceRequest,
  DecideRecommendationRequest,
  SuggestRecommendationsRequest,
  ContentFeedRun,
  SetContentIdeaStatusRequest,
  UpdateContentIdeaRequest,
} from '@repo/types'

import { gtmApi } from 'services'

const IDEAS_KEY = ['internal', 'gtm', 'content-ideas'] as const

export const useContentIdeas = () =>
  useQuery({
    queryKey: IDEAS_KEY,
    queryFn: () => gtmApi.contentIdeas(),
    staleTime: 30 * 1000,
    retry: false,
  })

/** One idea with its status history (the list carries no events). */
export const useContentIdea = (id: string | null) =>
  useQuery({
    queryKey: [...IDEAS_KEY, id],
    queryFn: () => gtmApi.contentIdea(id as string),
    enabled: !!id,
    staleTime: 15 * 1000,
    retry: false,
  })

/**
 * `TResult` is generic so a caller that needs the ANSWER gets it typed rather than `unknown` —
 * the feed below returns a tally the page puts in a snackbar. `TArgs` is `void` for a mutation
 * that takes none, so `mutateAsync()` needs no placeholder argument.
 */
const useIdeaMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => qc.invalidateQueries({ queryKey: IDEAS_KEY }),
  })
}

export const useCreateContentIdea = () => useIdeaMutation(gtmApi.createContentIdea)
export const useUpdateContentIdea = () =>
  useIdeaMutation(({ id, input }: { id: string; input: UpdateContentIdeaRequest }) =>
    gtmApi.updateContentIdea(id, input),
  )
export const useSetContentIdeaStatus = () =>
  useIdeaMutation(({ id, input }: { id: string; input: SetContentIdeaStatusRequest }) =>
    gtmApi.setContentIdeaStatus(id, input),
  )
export const useAddContentIdeaSource = () =>
  useIdeaMutation(({ id, input }: { id: string; input: AddContentIdeaSourceRequest }) =>
    gtmApi.addContentIdeaSource(id, input),
  )
export const useRemoveContentIdeaSource = () =>
  useIdeaMutation(({ id, itemId }: { id: string; itemId: string }) => gtmApi.removeContentIdeaSource(id, itemId))
// ContentFeedRun, not ContentIdeaFeedResult: VRG-741 made POST .../feed run BOTH halves (SEO +
// research corpus) and log them as one run row, so it returns the whole run. ContentIdeaFeedResult
// is now just the SEO half of it (`run.seo`). The declared type had stayed on the narrow shape,
// which forced an `as ContentFeedRun` at the one call site — removed with this.
export const useFeedContentIdeas = () =>
  useIdeaMutation<void, ContentFeedRun>(() => gtmApi.feedContentIdeas())

// VRG-741 — feed runs, unmapped corpus items (candidate keywords), recommendation cards. All keyed
// under IDEAS_KEY so any idea mutation (feed, suggest, decide) refreshes them.
export const useContentFeedRuns = () =>
  useQuery({
    queryKey: [...IDEAS_KEY, 'feed-runs'],
    queryFn: () => gtmApi.contentFeedRuns(),
    staleTime: 30 * 1000,
    retry: false,
  })

export const useContentUnmapped = (enabled: boolean) =>
  useQuery({
    queryKey: [...IDEAS_KEY, 'unmapped'],
    queryFn: () => gtmApi.contentUnmapped(),
    enabled,
    staleTime: 60 * 1000,
    retry: false,
  })

export const useRecommendationBatches = () =>
  useQuery({
    queryKey: [...IDEAS_KEY, 'batches'],
    queryFn: () => gtmApi.recommendationBatches(),
    staleTime: 30 * 1000,
    retry: false,
  })

export const useSuggestRecommendations = () =>
  useIdeaMutation((input: SuggestRecommendationsRequest) => gtmApi.suggestRecommendations(input))

export const useDecideContentRecommendation = () =>
  useIdeaMutation(({ id, input }: { id: string; input: DecideRecommendationRequest }) =>
    gtmApi.decideRecommendation(id, input),
  )

export const useContentIdeaKeywords = (q: string) =>
  useQuery({
    queryKey: [...IDEAS_KEY, 'keywords', q],
    queryFn: () => gtmApi.contentIdeaKeywords(q),
    enabled: q.trim().length >= 2,
    staleTime: 60 * 1000,
    retry: false,
  })

export const useContentIdeaCorpus = (q: string) =>
  useQuery({
    queryKey: [...IDEAS_KEY, 'corpus', q],
    queryFn: () => gtmApi.contentIdeaCorpus(q),
    enabled: q.trim().length >= 2,
    staleTime: 60 * 1000,
    retry: false,
  })
